import { BookOpenIcon, Check, ChevronDown, ChevronRight, Globe, Layers, ListVideo, type LucideIcon, Package, PenLine, Search, X } from "lucide-react";
import * as React from "react";
import { useSidePanel } from "@/components/contexts/SidePanelContext";
import Loading from "@/components/shared/Loading";
import { MessageParts } from "@/components/ui/message-parts";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ReasoningStep, ThinkingStep, ToolCall } from "@/hooks/useChat";
import { getDocumentIcon } from "@/lib/document-icons";
import { useMatterLabels } from "@/lib/useMatterLabels";
import { cn } from "@/lib/utils";
import { PROSE_INLINE_CODE_CLASSES } from "@/src/utils/message-rendering-classes";
import { formatElapsedDuration } from "@/utils/date-utils";
import { removeTimestampPrefix } from "@/utils/file-utils";
import { getActivityHeaderTool, isSpawnSpecialistTool } from "@/components/case-partner/components/assistantTurnTimeline";
import {
  getResearchAgentHeaderLabel,
  getResearchAgentHeaderParts,
  getResearchAgentActivity,
  getResearchAgentOutcome,
  type ResearchAgentOutcome,
  isWebResearchTool,
  researchAgentInFlight,
  specialistKindLabel,
  toolCallFailed,
} from "@/components/ui/researchAgentDisplay";
import { useSmoothStreamingContent } from "@/utils/streaming-utils";

const REASONING_PROSE_CLASSES = cn(
  "prose prose-xs text-xs prose-slate max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:text-slate-600 [&_strong]:font-semibold [&_strong]:text-slate-700 [&_pre]:bg-white [&_blockquote]:border-crimson-200 [&_a]:text-crimson-600 [&_a:hover]:text-crimson-700 [&_ul]:text-slate-600 [&_ol]:text-slate-600 [&_li]:text-slate-600 [&_h1]:my-0 [&_h1]:text-xs [&_h1]:font-semibold [&_h1]:leading-4 [&_h1]:text-slate-700 [&_h2]:my-0 [&_h2]:text-xs [&_h2]:font-semibold [&_h2]:leading-4 [&_h2]:text-slate-700 [&_h3]:my-0 [&_h3]:text-xs [&_h3]:font-semibold [&_h3]:leading-4 [&_h3]:text-slate-700",
  PROSE_INLINE_CODE_CLASSES,
);
const REASONING_ACCENT_CLASS = "bg-primary-400";
const REASONING_ICON_CLASS = "text-[#5F6C96]";
// An action the agent took. Peers with the prose around it, not a heading over
// it, so it carries just enough weight to be scannable.
const REASONING_STEP_LABEL_CLASS = "text-xs font-medium leading-4 text-slate-700";
const REASONING_STEP_PROSE_CLASS = "text-xs leading-5 text-slate-600";
const REASONING_DOCUMENT_ICON_CLASS = "text-[#6F7EAA]";
const REASONING_DOCUMENT_LINK_CLASS = "text-slate-700 hover:text-[#566494]";


// Tool name mappings for user-friendly display
const toolDisplayNames: Record<string, { pending: string; completed: string; failed: string; in_progress: string }> = {
  browse_case_files: {
    pending: "Browsing case files...",
    in_progress: "Browsing case files...",
    completed: "Browsed case files",
    failed: "Failed to browse case files"
  },
  read_files: {
    pending: "Reading files...",
    in_progress: "Reading files...",
    completed: "Read files",
    failed: "Failed to read files"
  },
  plan_document_access: {
    pending: "Planning document access...",
    in_progress: "Planning document access...",
    completed: "Planned document access",
    failed: "Failed to plan document access"
  },
  search_documents: {
    pending: "Searching documents...",
    in_progress: "Searching documents...",
    completed: "Searched documents",
    failed: "Failed to search documents"
  },
  list_draft_templates: {
    pending: "Finding draft templates...",
    in_progress: "Finding draft templates...",
    completed: "Found draft templates",
    failed: "Failed to find draft templates"
  },
  curate_draft_sources: {
    pending: "Curating draft sources...",
    in_progress: "Curating draft sources...",
    completed: "Curated draft sources",
    failed: "Failed to curate draft sources"
  },
  inspect_drafting_template: {
    pending: "Inspecting drafting template...",
    in_progress: "Inspecting drafting template...",
    completed: "Inspected drafting template",
    failed: "Failed to inspect drafting template"
  },
  inspect_draft: {
    pending: "Checking draft status...",
    in_progress: "Checking draft status...",
    completed: "Checked draft status",
    failed: "Failed to check draft status"
  },
  draft_work_product: {
    pending: "Preparing draft...",
    in_progress: "Queuing draft...",
    completed: "Draft queued",
    failed: "Failed to draft work product"
  },
  create_bundle: {
    pending: "Creating bundle...",
    in_progress: "Creating bundle...",
    completed: "Created bundle",
    failed: "Failed to create bundle"
  },
  list_deadlines: {
    pending: "Finding deadlines...",
    in_progress: "Finding deadlines...",
    completed: "Found deadlines",
    failed: "Failed to find deadlines"
  },
  list_dockets: {
    pending: "Listing linked dockets...",
    in_progress: "Listing linked dockets...",
    completed: "Listed linked dockets",
    failed: "Failed to list linked dockets"
  },
  search_docket_entries: {
    pending: "Searching docket entries...",
    in_progress: "Searching docket entries...",
    completed: "Searched docket entries",
    failed: "Failed to search docket entries"
  },
  get_docket_entries: {
    pending: "Reading docket entries...",
    in_progress: "Reading docket entries...",
    completed: "Read docket entries",
    failed: "Failed to read docket entries"
  },
  spawn_research: {
    pending: "Agent working...",
    in_progress: "Agent working...",
    completed: "Agent complete",
    failed: "Agent failed"
  },
  spawn_web_research: {
    pending: "Web Agent working...",
    in_progress: "Web Agent working...",
    completed: "Web Agent complete",
    failed: "Web Agent failed"
  },
  web_search: {
    pending: "Researching the public web...",
    in_progress: "Researching the public web...",
    completed: "Completed public research",
    failed: "Public web research failed"
  },
  // Default fallback for unknown tools
  default: {
    pending: "Preparing tool...",
    in_progress: "Running tool...",
    completed: "Tool completed",
    failed: "Tool failed"
  }
};

const IN_PROGRESS_DISPLAY_PREFIXES: Array<[RegExp, string]> = [
  [/^Browsing\b/, 'Browsed'],
  [/^Reading\b/, 'Read'],
  [/^Searching\b/, 'Searched'],
  [/^Finding\b/, 'Found'],
  [/^Listing\b/, 'Listed'],
  [/^Planning\b/, 'Planned'],
  [/^Inspecting\b/, 'Inspected'],
  [/^Checking\b/, 'Checked'],
  [/^Curating\b/, 'Curated'],
  [/^Preparing\b/, 'Prepared'],
  [/^Queuing\b/, 'Queued'],
  [/^Analysing\b/, 'Analysed'],
  [/^Analyzing\b/, 'Analyzed'],
  [/^Creating\b/, 'Created'],
  [/^Researching\b/, 'Researched'],
  [/^Consulting expert agent\b/, 'Consulted expert agent'],
  [/^Consulting research agent\b/, 'Consulted research agent'],
  [/^Running\b/, 'Ran'],
];

const LIVE_DISPLAY_PREFIXES: Array<[RegExp, string]> = [
  [/^Browsed\b/, 'Browsing'],
  [/^Read\b/, 'Reading'],
  [/^Searched\b/, 'Searching'],
  [/^Found\b/, 'Finding'],
  [/^Listed\b/, 'Listing'],
  [/^Planned\b/, 'Planning'],
  [/^Inspected\b/, 'Inspecting'],
  [/^Checked\b/, 'Checking'],
  [/^Curated\b/, 'Curating'],
  [/^Prepared\b/, 'Preparing'],
  [/^Queued\b/, 'Queuing'],
  [/^Analysed\b/, 'Analysing'],
  [/^Analyzed\b/, 'Analyzing'],
  [/^Created\b/, 'Creating'],
  [/^Researched\b/, 'Researching'],
  [/^Consulted expert agent\b/, 'Consulting expert agent'],
  [/^Consulted research agent\b/, 'Consulting research agent'],
  [/^Ran\b/, 'Running'],
];

const isDocumentSearchDisplay = (displayInfo: string, toolName?: string) => (
  toolName === 'search_documents'
  || /^(Searching|Searched|Finding|Found) documents\b/.test(displayInfo)
);

const documentSearchFamilyTitle = (labeled: string): string => (
  /^(Finding|Found) documents\b/.test(labeled) ? 'Found documents' : 'Searched documents'
);

export const formatBrowseFolderLabel = (displayInfo: string): string => {
  const match = displayInfo.match(/^((?:Browsing|Browsed) )(\/[^—]+?)(\s—.*)?$/);
  if (!match) return displayInfo;

  const [, verb, path, rest = ''] = match;
  const name = path
    .trim()
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .filter(Boolean)
    .map((segment) => segment.replace(/[-_]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()))
    .join(' / ');

  return name ? `${verb}${name}${rest}` : displayInfo;
};

export const formatToolDisplayInfo = (
  displayInfo: string,
  status?: string,
  _toolName?: string,
  isLive = false,
): string => {
  const labeled = formatBrowseFolderLabel(displayInfo);
  const actualStatus = status || 'completed';
  if (actualStatus === 'pending' || actualStatus === 'in_progress') {
    return labeled;
  }
  if (actualStatus !== 'completed') {
    return labeled;
  }

  // The chip pulse is tied to "this item is still live", not to the last
  // tool's completed status. Keep present tense until the pulse goes away.
  if (isLive) {
    for (const [pattern, replacement] of LIVE_DISPLAY_PREFIXES) {
      if (pattern.test(labeled)) {
        return labeled.replace(pattern, replacement);
      }
    }
    return labeled;
  }

  for (const [pattern, replacement] of IN_PROGRESS_DISPLAY_PREFIXES) {
    if (pattern.test(labeled)) {
      return labeled.replace(pattern, replacement);
    }
  }

  return labeled;
};

export const getToolDisplayText = (
  toolName: string,
  status?: string,
  displayInfo?: string,
  label?: ReturnType<typeof useMatterLabels>,
  isLive = false,
): string => {
  if (isSpawnSpecialistTool(toolName)) {
    return getResearchAgentHeaderLabel(1, status as ToolCall['status'], isLive, displayInfo, toolName);
  }

  if (displayInfo) {
    return formatToolDisplayInfo(displayInfo, status, toolName, isLive);
  }

  const actualStatus = status || 'completed';
  if (isLive && (actualStatus === 'completed' || toolName === 'search_documents')) {
    if (label && toolName === 'delegate_document_analysis') {
      return label('analysing-documents');
    }
    if (toolName === 'search_documents') {
      return toolDisplayNames.search_documents.in_progress;
    }
    const toolConfig = toolDisplayNames[toolName] || toolDisplayNames.default;
    return toolConfig.in_progress;
  }

  // Region-aware (analyse/analyze) copy for document analysis.
  if (label && toolName === 'delegate_document_analysis') {
    const analysisText: Record<string, string> = {
      pending: label('analysing-documents'),
      in_progress: label('analysing-documents'),
      completed: label('analysed-documents'),
      failed: label('failed-to-analyse-documents'),
    };
    return analysisText[actualStatus] || analysisText.completed;
  }

  const toolConfig = toolDisplayNames[toolName] || toolDisplayNames.default;
  return toolConfig[actualStatus as keyof typeof toolConfig] || toolConfig.completed;
};

type StepDotTone = 'failed' | 'current' | 'idle';

const OUTCOME_ICON: Record<ResearchAgentOutcome['tone'], LucideIcon> = {
  completed: Check,
  stopped: X,
  failed: X,
};

export type ChipHeaderStatus = {
  label: string;
  tone: 'working' | 'completed' | 'failed' | 'stopped';
};

const agentRowStatus = (
  outcome: ResearchAgentOutcome | null,
  liveStep?: string,
  live?: boolean,
): ChipHeaderStatus | null => {
  if (outcome) return { label: outcome.text, tone: outcome.tone };
  if (liveStep) return { label: liveStep, tone: 'working' };
  if (live) return { label: 'Working', tone: 'working' };
  return null;
};

// The dot carries the state and the line beside it carries the step, so a chip
// says what it is doing without its title having to change to say it.
const ChipStatus: React.FC<{ status: ChipHeaderStatus }> = ({ status }) => (
  <span className="flex min-w-0 items-center gap-1.5">
    <span
      aria-hidden="true"
      className={cn(
        'h-1.5 w-1.5 shrink-0 rounded-full',
        status.tone === 'completed' && 'bg-[#7DBF8E]',
        status.tone === 'working' && cn('crimson-reasoning-pulse', REASONING_ACCENT_CLASS),
        status.tone === 'failed' && 'bg-red-400',
        status.tone === 'stopped' && 'bg-slate-400',
      )}
    />
    <span
      className={cn(
        'truncate text-xs font-medium leading-5 text-slate-400',
        status.tone === 'working' && 'crimson-reasoning-shimmer',
      )}
    >
      {status.label.replace(/^working$/i, 'Working')}
    </span>
  </span>
);

const StepDot: React.FC<{ tone: StepDotTone }> = ({ tone }) => (
  <div className={cn(
    "absolute left-0 top-1.5 h-1.5 w-1.5 rounded-full transition-all duration-300",
    tone === 'failed'
      ? "bg-red-400"
      : tone === 'current'
        ? cn(
            "crimson-reasoning-pulse h-2 w-2 shadow-[0_0_0_3px_hsl(var(--primary-400)/0.16)]",
            REASONING_ACCENT_CLASS,
          )
        : "bg-slate-300",
  )} />
);

const stepDotTone = (
  tool: Pick<ToolCall, 'function' | 'status'>,
  { isCurrent }: { isCurrent: boolean },
): StepDotTone => {
  if (toolCallFailed(tool)) return 'failed';
  return isCurrent ? 'current' : 'idle';
};

export const getPlanAsReadingLabel = (
  displayInfo?: string,
  status?: string,
  isLive = false,
): string => {
  const countMatch = displayInfo?.match(/(\d+)\s+documents?/);
  const count = countMatch?.[1];
  const noun = count === '1' ? 'document' : 'documents';
  const target = count ? `${count} ${noun}` : noun;
  const inProgress = isLive || status === 'pending' || status === 'in_progress';
  return inProgress ? `Reading ${target}` : `Read ${target}`;
};

// The single line that stands for a run of tool calls. Shared by a lead chip's
// header and by an agent's grouped rows, so the same work reads the same way
// wherever it ran. A run of searches keeps the family name here; each query
// belongs on the row that did that search.
const activityGroupLabel = (
  steps: ThinkingStep[],
  isLive: boolean,
  label?: ReturnType<typeof useMatterLabels>,
): string => {
  const headerTool = getActivityHeaderTool(steps);
  if (!headerTool) return '';
  if (headerTool.function?.name === 'plan_document_access') {
    return getPlanAsReadingLabel(headerTool.displayInfo, headerTool.status, isLive);
  }
  const text = getToolDisplayText(
    headerTool.function?.name || 'default',
    headerTool.status,
    headerTool.displayInfo,
    label,
    isLive,
  );
  const searchCount = steps.filter((step) => (
    step.type === 'tool' && (step.data as ToolCall).function?.name === 'search_documents'
  )).length;
  if (
    !isLive
    && searchCount > 1
    && isDocumentSearchDisplay(text, headerTool.function?.name)
  ) {
    return documentSearchFamilyTitle(text);
  }
  return text;
};

// What a chip's label says about the call it is on right now, rather than about
// the work as a whole: the query being searched for, the filter being browsed
// under. The backend appends it after a colon or a dash.
const CHIP_LABEL_DETAIL = /^(.*?)\s*[:—–]\s+(.+)$/;

export const splitActivityLabel = (
  label: string,
): { title: string; detail?: string } => {
  const match = label.match(CHIP_LABEL_DETAIL);
  if (!match) {
    // A browse with no filter still arrives with the dash that would have
    // introduced one.
    return { title: label.replace(/\s*[:—–]\s*$/, '').trim() || label };
  }
  const [, title, detail] = match;
  if (!title.trim()) return { title: label };
  return { title: title.trim(), detail: detail.trim() };
};

const REASONING_HEADLINE_MARKUP = /^#{1,6}\s+|[*`]/g;

/**
 * The one line that stands for a reasoning summary.
 *
 * Summaries open with their own title, so the first line is what the paragraph
 * under it is about. A summary that opens with prose instead is clipped by the
 * layout, which is no worse than showing nothing.
 */
export const reasoningStepHeadline = (text?: string): string | undefined => {
  const first = (text || '')
    .split('\n')
    .map((line) => line.trim())
    .find(Boolean);
  if (!first) return undefined;
  const plain = first
    .replace(REASONING_HEADLINE_MARKUP, '')
    .replace(/\s+/g, ' ')
    .replace(/[:.]$/, '')
    .trim();
  return plain || undefined;
};

/**
 * How long this chip spent thinking, from the rounds of thought it holds.
 *
 * Each round reports once, on the last summary it produced, so the stretches a
 * chip covers add up rather than being counted per summary. Null when nothing
 * on the chip was timed, which is every turn recorded before rounds were
 * timed at all.
 */
export const activityThinkingDurationMs = (steps: ThinkingStep[]): number | null => {
  let total = 0;
  for (const step of steps) {
    if (step.type !== 'reasoning') continue;
    const durationMs = (step.data as ReasoningStep).duration_ms;
    if (typeof durationMs === 'number' && durationMs > 0) total += durationMs;
  }
  return total > 0 ? total : null;
};

const liveReasoningHeadline = (steps: ThinkingStep[]): string | undefined => {
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    const step = steps[index];
    if (step.type !== 'reasoning') continue;
    const headline = reasoningStepHeadline((step.data as ReasoningStep).text);
    if (headline) return headline;
  }
  return undefined;
};

/**
 * A chip's title and the step it is on, held apart.
 *
 * Chips used to say both in one line, which reads well for a single call and
 * badly for a run of them: the whole line rewrote itself on every step, so the
 * detail of the current one sat where the family name had been and the title
 * never held still. An agent chip already avoided that by naming its subject
 * and letting its current step change beside it, and this gives every other
 * chip the same shape. Only a live chip has a step to report. A settled search
 * drops back to the family name; other settled chips keep their label whole,
 * since that detail is then the only record of what they did.
 */
export const activityChipHeaderParts = (
  steps: ThinkingStep[],
  isLive: boolean,
  label?: ReturnType<typeof useMatterLabels>,
): { title: string; step?: string } => {
  const headerTool = getActivityHeaderTool(steps);
  if (!headerTool) {
    // A chip that has only thought: the reasoning's own opening line is the
    // nearest thing to a step it has.
    const step = isLive ? liveReasoningHeadline(steps) : undefined;
    return { title: '', ...(step ? { step } : {}) };
  }
  const full = activityGroupLabel(steps, isLive, label);
  if (!isLive) {
    if (isDocumentSearchDisplay(full, headerTool.function?.name)) {
      return { title: documentSearchFamilyTitle(full) };
    }
    return { title: full };
  }
  const { title, detail } = splitActivityLabel(full);
  return { title, ...(detail ? { step: detail } : {}) };
};

const AgentIcon = ListVideo;
const WebAgentIcon = Globe;

const getActivityHeaderIcon = (toolName?: string) => {
  switch (toolName) {
    case 'search_documents':
    case 'inspect_drafting_template':
    case 'inspect_draft':
    case 'delegate_document_analysis':
    case 'list_draft_templates':
    case 'browse_case_files':
    case 'list_deadlines':
    case 'create_deadline':
    case 'update_deadline':
    case 'delete_deadline':
      return Search;
    case 'read_files':
      return BookOpenIcon;
    case 'plan_document_access':
      return BookOpenIcon;
    case 'curate_draft_sources':
      return Layers;
    case 'draft_work_product':
      return PenLine;
    case 'create_bundle':
      return Package;
    case 'spawn_research':
      return AgentIcon;
    case 'spawn_web_research':
      return WebAgentIcon;
    case 'web_search':
      return Search;
    default:
      return BookOpenIcon;
  }
};

const ReasoningMarkdown: React.FC<{
  text: string;
  isStreaming: boolean;
  isComplete?: boolean;
  revealImmediately?: boolean;
}> = ({ text, isStreaming, isComplete, revealImmediately = false }) => {
  const shouldSmooth = isStreaming && !(isComplete ?? true);
  const smoothText = useSmoothStreamingContent(text, shouldSmooth, false, {
    startWithExistingContent: true,
    revealImmediately,
  });
  const displayText = shouldSmooth ? smoothText : text;

  return (
    <MessageParts
      className={REASONING_PROSE_CLASSES}
      content={displayText}
      hideCitations
      isStreaming={shouldSmooth}
    />
  );
};

interface SearchResultDisplay {
  guid?: string;
  document_guid?: string;
  filename?: string;
  page_number?: number | string | null;
  end_page_number?: number | string | null;
  page_numbers?: Array<number | string> | null;
}

const normalizeSearchResults = (output: unknown): SearchResultDisplay[] => {
  if (Array.isArray(output)) return output as SearchResultDisplay[];

  if (output && typeof output === 'object') {
    const data = output as { results?: unknown; results_metadata?: unknown };
    if (Array.isArray(data.results)) return data.results as SearchResultDisplay[];
    if (Array.isArray(data.results_metadata)) return data.results_metadata as SearchResultDisplay[];
  }

  return [];
};

const getSearchResultGuid = (result: SearchResultDisplay) => result.guid || result.document_guid || '';

const toPageNumber = (value: number | string | null | undefined) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const getSearchPageNumbers = (result: SearchResultDisplay) => {
  const explicitPages = result.page_numbers
    ?.map(toPageNumber)
    .filter((pageNumber): pageNumber is number => pageNumber !== undefined && pageNumber > 0);

  if (explicitPages && explicitPages.length > 0) {
    return [...new Set(explicitPages)].sort((a, b) => a - b);
  }

  const startPage = toPageNumber(result.page_number);
  const endPage = toPageNumber(result.end_page_number);

  if (!startPage) return endPage ? [endPage] : [];
  if (!endPage || endPage < startPage) return [startPage];

  return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
};

const formatPageLabel = (pages: number[]) => {
  if (pages.length === 0) return '';
  if (pages.length === 1) return `Page ${pages[0]}`;

  const ranges: string[] = [];
  let rangeStart = pages[0];
  let previousPage = pages[0];

  for (const page of pages.slice(1)) {
    if (page === previousPage + 1) {
      previousPage = page;
      continue;
    }

    ranges.push(rangeStart === previousPage ? String(rangeStart) : `${rangeStart}-${previousPage}`);
    rangeStart = page;
    previousPage = page;
  }

  ranges.push(rangeStart === previousPage ? String(rangeStart) : `${rangeStart}-${previousPage}`);
  return `Pages ${ranges.join(', ')}`;
};

interface MatchedDocumentDisplay {
  key: string;
  guid: string;
  filename: string;
  displayName: string;
  pageNumber?: number;
  pageNumbers: number[];
}

const getSearchMatchPageNumbers = (result: SearchResultDisplay) => {
  const startPage = toPageNumber(result.page_number);
  const endPage = toPageNumber(result.end_page_number);

  if (!startPage) return getSearchPageNumbers(result);
  if (!endPage || endPage < startPage) return [startPage];

  return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
};

const buildMatchedDocuments = (results: SearchResultDisplay[]) => {
  const documents: MatchedDocumentDisplay[] = [];
  const byDocument = new Map<string, MatchedDocumentDisplay>();

  for (const result of results) {
    const guid = getSearchResultGuid(result);
    const filename = result.filename || 'Unknown document';
    const displayName = removeTimestampPrefix(filename);
    const key = guid || filename;
    const matchPages = getSearchMatchPageNumbers(result);
    const pageNumber = toPageNumber(result.page_number) ?? matchPages[0];
    const existing = byDocument.get(key);

    if (existing) {
      existing.pageNumbers = [...new Set([...existing.pageNumbers, ...matchPages])].sort((a, b) => a - b);
      existing.pageNumber = existing.pageNumber ?? pageNumber;
      continue;
    }

    const document = {
      key,
      guid,
      filename,
      displayName,
      pageNumber,
      pageNumbers: matchPages,
    };
    byDocument.set(key, document);
    documents.push(document);
  }

  return documents;
};

const SearchResultsList: React.FC<{ results: SearchResultDisplay[] }> = ({ results }) => {
  const { openDocumentPanel } = useSidePanel();
  const [isOpen, setIsOpen] = React.useState(false);
  const documents = React.useMemo(() => buildMatchedDocuments(results), [results]);

  if (documents.length === 0) return null;

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="inline-flex items-center gap-1 text-[11px] font-medium leading-4 text-slate-500 hover:text-crimson-600"
      >
        {isOpen ? (
          <ChevronDown className="h-3 w-3 shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0" />
        )}
        <span>
          Top {documents.length} {documents.length === 1 ? 'match' : 'matches'}
        </span>
      </button>

      {isOpen && (
        <div className="mt-1 space-y-0.5 pl-4">
          {documents.map((document) => {
            const { Icon } = getDocumentIcon(document.filename);
            const pageLabel = formatPageLabel(document.pageNumbers);
            const pageBadge = pageLabel ? (
              <span className="shrink-0 text-[10px] leading-4 text-slate-400">
                {pageLabel}
              </span>
            ) : null;

            return (
              <div key={document.key} className="text-xs">
                {document.guid ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openDocumentPanel({
                        documentGuid: document.guid,
                        pageNumber: document.pageNumber,
                        activeTab: 'document',
                      });
                    }}
                    className={cn(
                      "flex max-w-full min-w-0 items-center gap-1.5 rounded-sm py-0.5 text-left text-[11px] leading-4",
                      REASONING_DOCUMENT_LINK_CLASS
                    )}
                    title={document.displayName}
                  >
                    <Icon className={cn("h-3 w-3 shrink-0", REASONING_DOCUMENT_ICON_CLASS)} />
                    <span className="min-w-0 truncate">{document.displayName}</span>
                    {pageBadge}
                  </button>
                ) : (
                  <div className="flex max-w-full min-w-0 items-center gap-1.5 rounded-sm py-0.5 text-[11px] leading-4 text-slate-600">
                    <Icon className={cn("h-3 w-3 shrink-0", REASONING_DOCUMENT_ICON_CLASS)} />
                    <span className="min-w-0 truncate" title={document.displayName}>{document.displayName}</span>
                    {pageBadge}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

type ToolStepDetail =
  | { kind: 'documents'; documents: { guid: string; filename: string }[] }
  | { kind: 'search'; results: ReturnType<typeof normalizeSearchResults> }
  | { kind: 'arguments'; args: Record<string, unknown> };

const TOOLS_THAT_SAY_IT_ALL_IN_THEIR_LABEL = new Set([
  'browse_case_files',
  'read_files',
  'delegate_document_analysis',
  'plan_document_access',
  'search_documents',
  'create_bundle',
  'list_draft_templates',
  'curate_draft_sources',
  'inspect_drafting_template',
  'inspect_draft',
  'draft_work_product',
  'spawn_research',
  'spawn_web_research',
  'web_search',
  'list_deadlines',
  'create_deadline',
  'update_deadline',
  'delete_deadline',
  'read_bundle',
  'edit_bundle',
]);

// Live tool outputs arrive as JSON strings; outputs reloaded from a stored chat
// are already parsed objects.
const parsedOrNull = (raw: unknown): unknown => {
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * What a tool step has to show beyond the label the chip already carries.
 *
 * Both the disclosure arrow and the expanded panel read this, so the arrow
 * cannot promise detail that is not there — an arrow that opens onto a copy of
 * the label costs a click to discover it was never worth one.
 */
export const toolStepDetail = (toolCall: ToolCall): ToolStepDetail | null => {
  const name = toolCall.function?.name ?? '';
  const output = toolCall.function?.output ? parsedOrNull(toolCall.function.output) : null;

  if (name === 'read_files' || name === 'delegate_document_analysis') {
    // Stored outputs can include draft_source_refs alongside the display documents.
    const wrapped = output && typeof output === 'object' && !Array.isArray(output)
      ? (output as { documents?: unknown }).documents
      : undefined;
    const raw = Array.isArray(output) ? output : Array.isArray(wrapped) ? wrapped : [output];
    const documents = raw.filter((document): document is { guid: string; filename: string } => (
      Boolean(document)
      && typeof document === 'object'
      && typeof (document as { filename?: unknown }).filename === 'string'
    ));
    return documents.length ? { kind: 'documents', documents } : null;
  }

  if (name === 'search_documents' && output) {
    const results = normalizeSearchResults(output);
    return results.length ? { kind: 'search', results } : null;
  }

  if (TOOLS_THAT_SAY_IT_ALL_IN_THEIR_LABEL.has(name)) return null;

  const args = toolCall.function?.arguments ? parsedOrNull(toolCall.function.arguments) : null;
  return args && typeof args === 'object' && Object.keys(args).length
    ? { kind: 'arguments', args: args as Record<string, unknown> }
    : null;
};

/** Whether opening this chip would show anything its label has not said. */
export const activityHasDisclosure = (steps: ThinkingStep[]): boolean => {
  if (steps.length !== 1) return steps.length > 1;
  const [step] = steps;
  if (step.type !== 'tool') return true;
  const toolCall = step.data as ToolCall;
  if (isSpawnSpecialistTool(toolCall.function?.name)) return true;
  return toolStepDetail(toolCall) !== null;
};

const chipTools = (steps: ThinkingStep[]): ToolCall[] => (
  steps
    .filter((step) => step.type === 'tool')
    .map((step) => step.data as ToolCall)
);

const toolStillRunning = (tool: ToolCall, isLive = false): boolean => {
  if (isSpawnSpecialistTool(tool.function?.name)) return researchAgentInFlight(tool, isLive);
  return tool.status === 'pending' || tool.status === 'in_progress';
};

/** Whether this chip's own work is still in flight, ignoring later lead thought. */
export const activityChipInFlight = (steps: ThinkingStep[], isLive = false): boolean => (
  chipTools(steps).some((tool) => toolStillRunning(tool, isLive))
);

/**
 * How the chip's tools ended, once they all have. Null while any are still
 * running, and for a thinking-only chip that never called a tool.
 */
export const activityChipOutcome = (
  steps: ThinkingStep[],
  isLive = false,
): ResearchAgentOutcome | null => {
  const tools = chipTools(steps);
  if (!tools.length || tools.some((tool) => toolStillRunning(tool, isLive))) return null;
  const research = [...tools].reverse().find((tool) => isSpawnSpecialistTool(tool.function?.name));
  if (research) return getResearchAgentOutcome(research);
  if (tools.some(toolCallFailed)) return { tone: 'failed', text: 'Could not complete' };
  if (tools.some((tool) => tool.status === 'stopped')) return { tone: 'stopped', text: 'Stopped' };
  return { tone: 'completed', text: 'Completed' };
};

const ToolStepDetailRows: React.FC<{
  detail: ToolStepDetail;
  onOpenDocument: (guid: string) => void;
}> = ({ detail, onOpenDocument }) => {
  if (detail.kind === 'search') return <SearchResultsList results={detail.results} />;
  if (detail.kind === 'arguments') {
    return (
      <details className="group">
        <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700">
          Input
        </summary>
        <pre className="mt-1 max-h-32 overflow-x-auto overflow-y-auto rounded-sm bg-white p-2 text-xs">
          {JSON.stringify(detail.args, null, 2)}
        </pre>
      </details>
    );
  }
  return (
    <div className="mt-2 space-y-1">
      {detail.documents.map((document) => {
        const { Icon } = getDocumentIcon(document.filename);
        const displayName = removeTimestampPrefix(document.filename);
        return (
          <div key={document.guid || document.filename} className="text-xs">
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onOpenDocument(document.guid);
              }}
              className={cn("flex cursor-pointer items-center gap-1.5", REASONING_DOCUMENT_LINK_CLASS)}
              title={displayName}
            >
              <Icon className={cn("h-3.5 w-3.5", REASONING_DOCUMENT_ICON_CLASS)} />
              <span>{displayName}</span>
            </button>
          </div>
        );
      })}
    </div>
  );
};

interface ReasoningStepsProps {
  thinkingSteps: ThinkingStep[];
  isStreaming?: boolean;
  isCurrentStep?: boolean;
  /** Reveals the rest of the received reasoning at once instead of typing it out. */
  revealImmediately?: boolean;
  className?: string;
  defaultExpanded?: boolean;
  pendingLabel?: string;
  reasoningDurationMs?: number | null;
}

export const ReasoningSteps: React.FC<ReasoningStepsProps> = ({
  thinkingSteps,
  isStreaming = false,
  isCurrentStep,
  revealImmediately = false,
  className,
  defaultExpanded = false,
  pendingLabel = 'Thinking',
  reasoningDurationMs,
}) => {
  const searchStillCurrent = isCurrentStep ?? isStreaming;
  const { openDocumentPanel } = useSidePanel();
  const label = useMatterLabels();
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const [autoScroll, setAutoScroll] = React.useState(true);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  // Auto-scroll to latest step when new steps are added
  React.useEffect(() => {
    if (autoScroll && scrollRef.current && thinkingSteps.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thinkingSteps, autoScroll]);

  // Reset expansion state when streaming starts
  React.useEffect(() => {
    if (isStreaming) {
      setAutoScroll(true);
    }
  }, [isStreaming]);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 10;
      setAutoScroll(isAtBottom);
    }
  };

  const hasSteps = thinkingSteps.length > 0;
  const canExpand = activityHasDisclosure(thinkingSteps);
  // The only step is a tool, and the chip header is already its label.
  const headerSaysItAll = hasSteps && thinkingSteps.length === 1
    && thinkingSteps[0].type === 'tool';

  const handleToggleExpanded = React.useCallback(() => {
    if (!canExpand) return;
    setIsExpanded((current) => !current);
  }, [canExpand]);

  if (!hasSteps && !isStreaming) {
    return null;
  }

  // What this chip's own rounds spent thinking, falling back to the figure the
  // turn reports for turns recorded before rounds were timed.
  const thoughtForMs = activityThinkingDurationMs(thinkingSteps) ?? reasoningDurationMs;
  const hasDuration = typeof thoughtForMs === 'number' && Number.isFinite(thoughtForMs);
  const headerTool = getActivityHeaderTool(thinkingSteps);
  const researchTools = thinkingSteps
    .filter((step) => step.type === 'tool' && isSpawnSpecialistTool((step.data as ToolCall).function?.name))
    .map((step) => step.data as ToolCall);
  const researchAgentCount = researchTools.length;
  const researchLive = isStreaming && researchTools.some((tool) => researchAgentInFlight(tool, isStreaming));
  const researchHeaderTool = [...researchTools].reverse().find((tool) => researchAgentInFlight(tool, isStreaming))
    ?? researchTools[researchTools.length - 1]
    ?? headerTool;
  const researchHeader = isSpawnSpecialistTool(headerTool?.function?.name)
    ? getResearchAgentHeaderParts(researchHeaderTool, Math.max(1, researchAgentCount), isStreaming)
    : null;
  // Whether this chip's own work is still going, which is what both its label
  // and its badge answer to. The turn can keep streaming long after the chip
  // has settled, so asking that instead left a tick beside "Searching...".
  const toolsInFlight = activityChipInFlight(thinkingSteps, isStreaming);
  const chipIsLive = isStreaming && toolsInFlight;
  const showLivePulse = chipIsLive || (isStreaming && !headerTool);
  const chipHeader = activityChipHeaderParts(thinkingSteps, showLivePulse, label);
  const toolLabel = researchHeader ? researchHeader.title : chipHeader.title;
  const headerLabel = toolLabel
    ? toolLabel
    : hasSteps
      ? isStreaming
        ? 'Thinking'
        : hasDuration
          ? `Thought for ${formatElapsedDuration(thoughtForMs)}`
          : 'Thought'
      : pendingLabel;
  const HeaderIcon = getActivityHeaderIcon(headerTool?.function?.name);
  const isAgentRow = isSpawnSpecialistTool(headerTool?.function?.name);
  const isWebAgent = isWebResearchTool(headerTool?.function?.name);
  const namedAgentTitle = researchHeader?.title.match(/^(.*)\s+agent$/i)?.[1];
  const chipOutcome = activityChipOutcome(thinkingSteps, isStreaming);
  // Agents keep a completed/failed/stopped status beside the title. Other
  // chips already say that in past tense, so only a working one has a step
  // worth a line of its own.
  const chipStatus: ChipHeaderStatus | null = isAgentRow
    ? agentRowStatus(chipOutcome, researchHeader?.status, researchLive)
    : chipHeader.step
      ? { label: chipHeader.step, tone: 'working' }
      : null;
  // One live marker per chip: the inline dot stands in for a working step
  // beside the title, and the title holds still while that step shimmers.
  const showIconPulse = showLivePulse && !chipStatus;

  return (
    <div className={cn(
      "flex max-w-full flex-col leading-none",
      className
    )}>
      <button
        type="button"
        onClick={handleToggleExpanded}
        aria-disabled={!canExpand}
        className={cn(
          "group flex max-w-full items-center gap-2 rounded-full pr-1.5 text-left leading-none transition-colors",
          isAgentRow && "w-fit",
          canExpand ? "hover:bg-slate-50/70" : "cursor-default",
        )}
      >
        {isAgentRow ? (
          <>
            <HeaderIcon
              className={cn('h-[18px] w-[18px] shrink-0', REASONING_ICON_CLASS)}
            />
            <span className="min-w-0 truncate text-xs font-medium leading-5 text-slate-500">
              {namedAgentTitle || headerLabel}
              {namedAgentTitle && (
                <span> · {isWebAgent ? 'Web Agent' : 'Agent'}</span>
              )}
            </span>
            {chipStatus && <ChipStatus status={chipStatus} />}
            {canExpand && (
              <span className="ml-0.5 shrink-0 text-slate-400 transition-colors group-hover:text-slate-600">
                {isExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </span>
            )}
          </>
        ) : (
          <>
        {headerTool && (
        <div className="relative flex h-[18px] w-[18px] shrink-0 items-center justify-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HeaderIcon
                  className={cn(
                    HeaderIcon === Search ? 'h-[15px] w-[15px]' : 'h-[18px] w-[18px]',
                    REASONING_ICON_CLASS,
                  )}
                />
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start" className="max-w-md">
                <p className="text-xs">
                  Case Partner's reasoning steps and actions for this response.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {showIconPulse && (
            <span
              aria-hidden="true"
              className={cn("-right-0.5 -top-1 absolute h-2 w-2 rounded-full", REASONING_ACCENT_CLASS, "crimson-reasoning-pulse")}
            />
          )}
        </div>
        )}

        <span className={cn(
          "min-w-0 truncate text-xs font-medium leading-5 text-slate-500",
          showIconPulse && "crimson-reasoning-shimmer"
        )}>{headerLabel}</span>
        {chipStatus && <ChipStatus status={chipStatus} />}

        {canExpand && (
          <span className="ml-0.5 shrink-0 text-slate-400 transition-colors group-hover:text-slate-600">
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </span>
        )}
          </>
        )}
      </button>

      {isExpanded && canExpand && (
        <div
          className="mt-2 max-w-4xl border-l border-slate-200/80 pl-3 transition-all duration-300"
        >
          <div
            className="max-h-96 overflow-y-auto pr-2"
            ref={scrollRef}
            onScroll={handleScroll}
          >
            {!hasSteps && isStreaming ? (
              <div className="py-2">
                <Loading dotClassName={REASONING_ACCENT_CLASS} size="sm" inline />
              </div>
            ) : (
              <div className="space-y-3 py-1">
                {thinkingSteps.map((item, idx) => {
                  if (researchAgentCount > 0 && item.type === 'reasoning') {
                    return null;
                  }
                  const isLastItem = idx === thinkingSteps.length - 1;
                  const itemKey = item.type === 'tool'
                    ? `tool-${(item.data as ToolCall).call_id || (item.data as ToolCall).function?.name || 'unknown'}-${item.round_num ?? 'round'}`
                    : `reasoning-${item.round_num ?? 'round'}-${item.summary_index ?? 'summary'}`;

                  if (item.type === 'tool' && isSpawnSpecialistTool((item.data as ToolCall).function?.name)) {
                    const toolCall = item.data as ToolCall;
                    const inFlight = researchAgentInFlight(toolCall, isStreaming);
                    const activity = getResearchAgentActivity(toolCall);
                    const outcome = getResearchAgentOutcome(toolCall);
                    const awaitingFirstStep = activity.length === 0 && inFlight;
                    return (
                      <React.Fragment key={itemKey}>
                        {awaitingFirstStep ? (
                          <div className="relative pl-5 transition-opacity duration-300">
                            <StepDot tone={toolCallFailed(toolCall) ? 'failed' : 'current'} />
                            <div className={REASONING_STEP_LABEL_CLASS}>
                              {specialistKindLabel(toolCall.function?.name)} working...
                            </div>
                          </div>
                        ) : null}
                        {activity.map((row, rowIdx) => {
                          const isLastRow = rowIdx === activity.length - 1;
                          if (row.kind === 'commentary') {
                            return (
                              <div
                                key={`${itemKey}-${row.id}`}
                                className="relative pl-5 transition-opacity duration-300"
                              >
                                <div className={REASONING_STEP_PROSE_CLASS}>{row.text}</div>
                              </div>
                            );
                          }
                          const groupTools = row.steps.map((step) => step.data as ToolCall);
                          const lastTool = groupTools[groupTools.length - 1];
                          const isCurrent = isStreaming
                            && isLastRow
                            && (lastTool?.status === 'pending' || lastTool?.status === 'in_progress');
                          // A failure anywhere in the run is what the row has to
                          // report, even when a later call in it succeeded.
                          const representative = groupTools.find(toolCallFailed) ?? lastTool;
                          return (
                            <div
                              key={`${itemKey}-${row.id}`}
                              className="relative pl-5 transition-opacity duration-300"
                            >
                              <StepDot tone={stepDotTone(representative, { isCurrent })} />
                              <div className={REASONING_STEP_LABEL_CLASS}>
                                {activityGroupLabel(row.steps, isCurrent, label)}
                              </div>
                            </div>
                          );
                        })}
                        {outcome ? (
                          // Closes the agent's run, so it sits in the step
                          // column with the work it is closing. The mark takes
                          // the place a step dot would, since this is an ending
                          // rather than another thing the agent did.
                          <div className="relative pl-5">
                            {/* The mark is centred against the label's own line
                                box, and on the step dots' column, so it stays
                                aligned if either size changes. The label has to
                                be a block of its own for that to hold: as an
                                inline it would sit in a line box sized by the
                                inherited line height, not by its own. */}
                            <span className="absolute left-0 top-0 flex h-4 w-1.5 items-center justify-center">
                              {React.createElement(OUTCOME_ICON[outcome.tone], {
                                'aria-hidden': true,
                                className: cn(
                                  'h-3 w-3 shrink-0',
                                  outcome.tone === 'failed' ? 'text-red-400' : 'text-slate-400',
                                ),
                              })}
                            </span>
                            <div className="text-xs leading-4 text-slate-400">
                              {outcome.text}
                            </div>
                          </div>
                        ) : null}
                      </React.Fragment>
                    );
                  }

                  return (
                    <div
                      key={itemKey}
                      className="relative pl-5 transition-opacity duration-300"
                    >
                      {item.type === 'reasoning' ? (
                        <>
                          {!((item.data as ReasoningStep).isComplete ?? true)
                            && isLastItem
                            && isStreaming && <StepDot tone="current" />}

                          <div className={REASONING_STEP_PROSE_CLASS}>
                            {(item.data as ReasoningStep).text ? (
                              <ReasoningMarkdown
                                text={(item.data as ReasoningStep).text}
                                isStreaming={isStreaming}
                                isComplete={(item.data as ReasoningStep).isComplete}
                                revealImmediately={revealImmediately}
                              />
                            ) : (
                              <span className="text-slate-400 italic">Reasoning...</span>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <StepDot tone={stepDotTone(item.data as ToolCall, {
                            isCurrent: isStreaming
                              && ((item.data as ToolCall).status === 'pending'
                                || (item.data as ToolCall).status === 'in_progress'),
                          })} />

                          <div className="space-y-1">
                            {!headerSaysItAll && (
                              <div className={REASONING_STEP_LABEL_CLASS}>
                                {getToolDisplayText(
                                  (item.data as ToolCall).function?.name || 'default',
                                  (item.data as ToolCall).status,
                                  (item.data as ToolCall).displayInfo,
                                  label,
                                  searchStillCurrent && isLastItem,
                                )}
                              </div>
                            )}

                            {(() => {
                              const detail = toolStepDetail(item.data as ToolCall);
                              if (!detail) return null;
                              return (
                                <ToolStepDetailRows
                                  detail={detail}
                                  onOpenDocument={(guid) => openDocumentPanel({
                                    documentGuid: guid,
                                    activeTab: 'document',
                                  })}
                                />
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default ReasoningSteps;
